import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { searchBackofficeUsers } from '../api';
import type {
  BackofficeUserSearchRow,
  OrganizationMemberDelivery,
  OrganizationMemberInput,
  OrganizationMemberRole,
  OrganizationMembershipStatus,
} from '../types';
import {
  getOrganizationMemberRoleOptions,
  normalizeOrganizationMemberForRole,
  normalizeOrganizationMemberInput,
  ORGANIZATION_MEMBER_EMAIL_PATTERN,
} from './organization-member-form';

export interface OrganizationMemberEditorProps {
  organizationId?: string;
  members: OrganizationMemberInput[];
  onMembersChange: (members: OrganizationMemberInput[]) => void;
  allowedRoles: OrganizationMemberRole[];
  maxMembers?: number;
  searchLabel?: string;
}

export function OrganizationMemberEditor({
  organizationId,
  members,
  onMembersChange,
  allowedRoles,
  maxMembers,
  searchLabel = 'Search existing users or enter an email',
}: OrganizationMemberEditorProps) {
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<BackofficeUserSearchRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [resolvedSearch, setResolvedSearch] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<OrganizationMembershipStatus | undefined>();

  const allowOwner = allowedRoles.includes('owner');
  const roleOptions = useMemo(
    () =>
      getOrganizationMemberRoleOptions(allowOwner).filter((option) =>
        allowedRoles.includes(option.value)
      ),
    [allowOwner, allowedRoles]
  );
  const canInviteEmail = ORGANIZATION_MEMBER_EMAIL_PATTERN.test(userSearch.trim());
  const canAddMore = maxMembers === undefined || members.length < maxMembers;
  const searchReady =
    resolvedSearch === `${organizationId ?? ''}:${userSearch.trim()}` && !searchError;

  function statusLabel(status: OrganizationMembershipStatus | undefined) {
    return status === 'member'
      ? 'Already a member'
      : status === 'pending_invite'
        ? 'Pending invite'
        : null;
  }

  function canSelect(status: OrganizationMembershipStatus | undefined) {
    return (!organizationId || (searchReady && status === 'available')) && !statusLabel(status);
  }

  useEffect(() => {
    const trimmed = userSearch.trim();
    setResolvedSearch(null);
    setEmailStatus(undefined);
    setUserResults([]);
    if (trimmed.length < 2 || !canAddMore) {
      setUserResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    setSearchLoading(true);
    const timeout = window.setTimeout(() => {
      setSearchLoading(true);
      setSearchError(null);
      searchBackofficeUsers(trimmed, controller.signal, organizationId)
        .then((response) => {
          if (controller.signal.aborted) return;
          if (
            organizationId &&
            (!response.emailMembershipStatus ||
              response.users.some((user) => !user.membershipStatus))
          ) {
            throw new Error('Unable to verify organization membership. Please try again.');
          }
          setUserResults(response.users);
          setEmailStatus(response.emailMembershipStatus);
          setResolvedSearch(`${organizationId ?? ''}:${trimmed}`);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          if (caught instanceof DOMException && caught.name === 'AbortError') {
            return;
          }
          setSearchError(caught instanceof Error ? caught.message : 'Failed to search users');
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setSearchLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [canAddMore, organizationId, userSearch]);

  function setMembers(nextMembers: OrganizationMemberInput[]) {
    onMembersChange(nextMembers.map((member) => normalizeOrganizationMemberInput(member)));
  }

  function addExistingUser(user: BackofficeUserSearchRow) {
    if (!canAddMore || !canSelect(user.membershipStatus)) {
      return;
    }
    if (members.some((member) => member.userId === user.id || member.email === user.email)) {
      return;
    }

    setMembers([
      ...members,
      {
        kind: 'existing',
        userId: user.id,
        name: user.name,
        email: user.email,
        role: members.length === 0 && allowOwner ? 'owner' : 'member',
        delivery: 'direct',
      },
    ]);
    setUserSearch('');
    setUserResults([]);
    setSearchError(null);
  }

  function addEmailInvite(email: string) {
    if (!canAddMore || !canSelect(emailStatus)) {
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!ORGANIZATION_MEMBER_EMAIL_PATTERN.test(normalizedEmail)) {
      return;
    }
    if (members.some((member) => member.email.toLowerCase() === normalizedEmail)) {
      return;
    }

    setMembers([
      ...members,
      {
        kind: 'email',
        email: normalizedEmail,
        name: null,
        role: 'member',
        delivery: 'invite',
      },
    ]);
    setUserSearch('');
    setUserResults([]);
    setSearchError(null);
  }

  function updateMember(index: number, updates: Partial<OrganizationMemberInput>) {
    setMembers(
      members.map((member, memberIndex) => {
        if (memberIndex !== index) {
          return member;
        }
        return {
          ...member,
          ...updates,
        };
      })
    );
  }

  function removeMember(index: number) {
    setMembers(members.filter((_, memberIndex) => memberIndex !== index));
  }

  function isRoleDisabled(member: OrganizationMemberInput) {
    return member.kind === 'email';
  }

  function isDeliveryDisabled(member: OrganizationMemberInput) {
    return member.kind === 'email' || member.role === 'owner';
  }

  return (
    <div className="space-y-5">
      <label className="block">
        <span className="text-small font-medium text-neutral-700">{searchLabel}</span>
        <div className="mt-1 flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2">
          <Search className="size-4 text-neutral-400" />
          <input
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            className="min-w-0 flex-1 border-0 text-body outline-none"
            placeholder="name@example.com"
            disabled={!canAddMore}
          />
        </div>
      </label>

      {!canAddMore ? (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-small text-neutral-600">
          Remove the current member before adding another.
        </div>
      ) : null}

      {searchError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-800">
          {searchError}
        </div>
      ) : null}

      {userSearch.trim().length >= 2 && canAddMore ? (
        <div className="rounded-lg border border-neutral-200">
          {searchLoading ? (
            <div className="px-4 py-3 text-small text-neutral-500">Searching users</div>
          ) : userResults.length > 0 ? (
            userResults.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => addExistingUser(user)}
                disabled={!canSelect(user.membershipStatus)}
                className="flex w-full items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:bg-neutral-50"
              >
                <span>
                  <span className="block text-small font-medium text-neutral-900">
                    {user.name ?? user.email}
                  </span>
                  <span className="block text-xs text-neutral-500">{user.email}</span>
                </span>
                {statusLabel(user.membershipStatus) ? (
                  <span className="shrink-0 text-xs text-neutral-500">
                    {statusLabel(user.membershipStatus)}
                  </span>
                ) : (
                  <Plus className="size-4 text-neutral-500" />
                )}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-small text-neutral-500">No users found</div>
          )}
          {canInviteEmail ? (
            <button
              type="button"
              onClick={() => addEmailInvite(userSearch)}
              disabled={!canSelect(emailStatus)}
              className="flex w-full items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3 text-left hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:bg-neutral-50"
            >
              <span className="text-small font-medium text-neutral-900">
                Invite {userSearch.trim().toLowerCase()}
              </span>
              {statusLabel(emailStatus) ? (
                <span className="shrink-0 text-xs text-neutral-500">
                  {statusLabel(emailStatus)}
                </span>
              ) : (
                <Plus className="size-4 text-neutral-500" />
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-3">
        {members.map((member, index) => (
          <div
            key={`${member.email}-${member.userId ?? index}`}
            className="rounded-lg border border-neutral-200 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-small font-semibold text-neutral-900">
                  {member.name ?? member.email}
                </div>
                <div className="truncate text-xs text-neutral-500">{member.email}</div>
              </div>
              <button
                type="button"
                onClick={() => removeMember(index)}
                className="rounded-md p-1 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
                aria-label={`Remove ${member.email}`}
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-neutral-600">Role</span>
                <select
                  value={member.role}
                  onChange={(event) =>
                    updateMember(
                      index,
                      normalizeOrganizationMemberForRole(
                        member,
                        event.target.value as OrganizationMemberRole
                      )
                    )
                  }
                  disabled={isRoleDisabled(member)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-small outline-none focus:border-neutral-900 disabled:bg-neutral-100"
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-neutral-600">Add method</span>
                <select
                  value={member.delivery}
                  onChange={(event) =>
                    updateMember(index, {
                      delivery: event.target.value as OrganizationMemberDelivery,
                    })
                  }
                  disabled={isDeliveryDisabled(member)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-small outline-none focus:border-neutral-900 disabled:bg-neutral-100"
                >
                  <option value="direct">Direct</option>
                  <option value="invite">Invite</option>
                </select>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
